import { IRawStyle, MessageBar, MessageBarType } from "@fluentui/react";
import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
    ButtonRenderProps,
    ContentSection,
    FormValidationSummary,
    Optional,
    Shimmer,
    Stack,
    WarningMessage
} from "sprint-common-web";

import {
    CrossTypeContentSearchItem,
    CrossTypeContentSearch,
    CrossTypeContentSearchMovieItem,
    CrossTypeContentSearchMovieItemVersion,
    CrossTypeContentSearchTvSeriesItem,
    CrossTypeContentSearchTvSeriesItemSeason,
    CrossTypeContentSearchTvSeriesItemEpisode,
    CrossTypeContentSearchTvSeriesItemEpisodeVersion,
    CrossTypeContentSearchAncillaryItem
} from "../../../../common/components/form/CrossTypeContentSearch";
import { ContentReferenceType } from "../../../../common/types/ContentReferenceType";
import { ContentSource } from "../../../../common/types/ContentSource";
import { ContentSubtype } from "../../../../common/types/ContentSubtype";
import { mapToMovieContentSubtype } from "../../../../common/types/MovieContentSubtype";
import { tvSeriesContentSubtypes } from "../../../../common/types/TvSeriesContentSubtype";
import { CloudFileAssociationContentDetails } from "../../../cloud-files/details/sections/associate/context/CloudFileAssociationContentDetails";
import { ContentAssociationAncillaryContentForm } from "../ancillary/ContentAssociationAncillaryContentForm";
import { AssociateLookupActionsButton } from "../buttons/lookups/AssociateLookupActionsButton";
import { AssociateLookupOrdersButton } from "../buttons/lookups/AssociateLookupOrdersButton";
import { AssociateContentFormContext } from "../context/AssociateContentFormContext";
import { useAssociateContentFormFields } from "../hooks/useAssociateContentFormFields";
import { ContentAssociationMovieForm } from "../movie/ContentAssociationMovieForm";
import { ContentAssociationTvContentForm } from "../tv-series/ContentAssociationTvContentForm";
import {
    ContentAssociationValidation,
    defaultContentAssociationValidation
} from "../types/AssociateContentFormValidationRules";
import {
    ContentAssociationAncillaryDetails,
    ContentAssociationDetails,
    ContentAssociationMovieDetails,
    ContentAssociationTvSeriesDetails
} from "../types/ContentAssociationDetails";
import { ContentAssociationFormState } from "../types/ContentAssociationFormState";
import { checkIsInactiveContentSelected, inactiveContentMessage } from "../utils/checkIsInactiveContentSelected";

export interface AssociateContentFieldsProps {
    formTitle?: string;
    isEditDisabled?: boolean;
    isDefaultContentLoading: boolean;
    defaultContentState?: ContentAssociationFormState;
    validation?: ContentAssociationValidation;
    onInactiveContentSelectionChange?: (value: boolean) => void;
    renderLabelSection?: boolean;
    infoMessage?: string;
    style?: IRawStyle;
    children?: React.ReactNode;
}

const contentRelatedFormFieldNames: (keyof ContentAssociationFormState)[] = [
    "contentType",
    "contentId",
    "versionIds",
    "seasonId",
    "episodeId",
    "airlineId"
];

export const AssociateContentFormFields = (props: AssociateContentFieldsProps): JSX.Element => {
    const {
        formTitle,
        isDefaultContentLoading,
        defaultContentState,
        onInactiveContentSelectionChange,
        validation = defaultContentAssociationValidation,
        infoMessage,
        style
    } = props;

    const {
        isFormLoading,
        setIsFormLoading,
        contentDetails,
        setContentDetails,
        hasLoadedInitialContentRef,
        initContentState,
        setContentState,
        setValidationResult,
        validationResult
    } = useAssociateContentFormFields();

    const { watch, setValue, clearErrors, control, errors } = useFormContext<ContentAssociationFormState>();

    const contentRelatedFormValues: ContentAssociationFormState = watch(contentRelatedFormFieldNames);

    const { contentId: formContentId, contentType: formContentType } = contentRelatedFormValues;

    const isInactiveContentSelected = checkIsInactiveContentSelected(contentDetails, contentRelatedFormValues);

    const initOptions = useMemo(() => {
        return [getContentSearchInitOption(contentDetails)];
    }, [contentDetails]);

    useEffect(() => {
        setIsFormLoading(true);

        initContentState(defaultContentState).finally(() => {
            hasLoadedInitialContentRef.current = true;
            setIsFormLoading(false);
        });
    }, [defaultContentState?.contentId, defaultContentState?.contentType]);

    useEffect(() => {
        if (onInactiveContentSelectionChange) {
            onInactiveContentSelectionChange(isInactiveContentSelected);
        }
    }, [isInactiveContentSelected]);

    useEffect(() => {
        if (!formContentId || !formContentType) {
            setContentDetails(undefined);
            return;
        }

        if (isManualChange(formContentId, formContentType)) {
            return;
        }

        setIsFormLoading(true);

        setContentState(formContentId, formContentType).finally(() => {
            setIsFormLoading(false);
        });
    }, [formContentId, formContentType]);

    const isManualChange = (contentId: string, contentType: ContentReferenceType) => {
        return contentDetails && contentDetails.id === contentId && contentDetails.type === contentType;
    };

    const onTitleSelect = (content: CrossTypeContentSearchItem) => {
        setContentDetails(mapContentDetails(content));

        resetForm(content.id, content.type);

        if (setValidationResult) {
            setValidationResult(undefined);
        }
    };

    const onClear = () => {
        setContentDetails(undefined);
        setValue("contentType", undefined);

        clearErrors(contentRelatedFormFieldNames);
    };

    const resetForm = (contentId: string, contentType: ContentReferenceType) => {
        setValue("contentType", contentType);
        setValue("contentId", contentId);
        setValue("seasonId", undefined);
        setValue("episodeId", undefined);
        setValue("versionIds", []);

        clearErrors(contentRelatedFormFieldNames);
    };

    const shouldRenderSection = props.renderLabelSection ?? true;

    const isInitializingContent =
        !hasLoadedInitialContentRef.current && !!defaultContentState?.contentType && !contentDetails;

    const isLoading = isDefaultContentLoading || isFormLoading || isInitializingContent;
    const isEditDisabled = props.isEditDisabled ?? false;

    const formContent = (
        <>
            {isInactiveContentSelected && getInactiveContentMessageBar()}
            {infoMessage && <WarningMessage message={infoMessage} />}
            <CrossTypeContentSearch
                name="contentId"
                testId="association-content-search"
                label="Content search"
                placeholder="Title"
                disabled={isEditDisabled}
                initOptions={initOptions}
                control={control}
                errors={errors}
                onSelect={onTitleSelect}
                onClear={onClear}
                required={validation.rules.isTitleRequired}
            />
            <Stack verticalAlign="space-between">{renderFormBySelectedContentType(contentDetails?.type)}</Stack>
            {props.children}
            <FormValidationSummary validationResult={validationResult} />
        </>
    );

    const lookupOrdersButton: ButtonRenderProps = {
        render: AssociateLookupOrdersButton
    };

    const lookupActionsButton: ButtonRenderProps = {
        render: AssociateLookupActionsButton
    };

    return (
        <AssociateContentFormContext.Provider
            value={{
                contentDetails,
                validation,
                setValidationResult,
                isEditDisabled
            }}
        >
            {shouldRenderSection ? (
                <ContentSection
                    header={formTitle ?? "Associate"}
                    style={style}
                    isLoading={isLoading}
                    buttons={[lookupOrdersButton, lookupActionsButton]}
                >
                    {formContent}
                </ContentSection>
            ) : (
                <Stack>
                    <div style={{ display: isLoading ? "none" : undefined }}>{formContent}</div>
                    {isLoading && <Shimmer />}
                </Stack>
            )}
        </AssociateContentFormContext.Provider>
    );
};

const getInactiveContentMessageBar = (): JSX.Element => (
    <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
        {inactiveContentMessage}
    </MessageBar>
);

const renderFormBySelectedContentType = (contentDetailsType?: ContentReferenceType) => {
    if (!contentDetailsType) {
        return <></>;
    }

    switch (contentDetailsType) {
        case ContentReferenceType.Movie:
            return <ContentAssociationMovieForm />;

        case ContentReferenceType.TvSeries:
            return <ContentAssociationTvContentForm />;

        case ContentReferenceType.Ancillary:
            return <ContentAssociationAncillaryContentForm />;

        default:
            return <></>;
    }
};

const getContentSearchInitOption = (contentDetails: Optional<ContentAssociationDetails>) => {
    const opt = {
        key: "",
        text: ""
    };

    if (contentDetails) {
        const subtype = contentDetails?.subtype ? ` [${ContentSubtype[contentDetails.subtype]}]` : "";
        const source = contentDetails?.source ? ` [${ContentSource[contentDetails.source]}]` : "";

        opt.key = contentDetails.id;
        opt.text = `${contentDetails.title}${subtype}${source}`;

        return opt;
    }

    return opt;
};

const mapContentDetails = (details: CrossTypeContentSearchItem): CloudFileAssociationContentDetails => {
    switch (details.type) {
        case ContentReferenceType.Movie:
            return mapToMovieDetails(details);

        case ContentReferenceType.TvSeries:
            return mapToTvSeriesDetails(details);

        case ContentReferenceType.Ancillary:
            return mapToAncillaryDetails(details);

        default:
            throw new Error("Unsupported content type");
    }
};

// /* movie */
const mapToMovieDetails = (movie: CrossTypeContentSearchMovieItem): ContentAssociationMovieDetails => {
    return {
        type: ContentReferenceType.Movie,
        id: movie.id,
        title: movie.title,
        subtype: mapToMovieContentSubtype(movie.subtype),
        source: movie.source,
        buzz: movie.buzz && {
            id: movie.buzz.id,
            isArchived: movie.buzz.isArchived,
            isDeleted: movie.buzz.isDeleted
        },
        versions: movie.versions.map((v) => mapToMovieVersionDetails(v))
    };
};

const mapToMovieVersionDetails = (version: CrossTypeContentSearchMovieItemVersion) => {
    return {
        type: version.type,
        id: version.id,
        distributor: version.distributor,
        brandings: version.brandings,
        buzzVersion: version.buzzVersion && {
            id: version.buzzVersion.id,
            isArchived: version.buzzVersion.isArchived,
            name: version.buzzVersion.name,
            notes: version.buzzVersion.notes
        },
        shreqVersion: version.shreqVersion && {
            id: version.shreqVersion.id,
            name: version.shreqVersion.name
        },
        mergedTargetId: version.mergedTargetId
    };
};

// /* tv */
const mapToTvSeriesDetails = (tvSeries: CrossTypeContentSearchTvSeriesItem): ContentAssociationTvSeriesDetails => {
    return {
        type: ContentReferenceType.TvSeries,
        subtype: tvSeriesContentSubtypes[0],
        source: tvSeries.source,
        id: tvSeries.id,
        title: tvSeries.title,
        orphaned: tvSeries.orphaned,
        buzzId: tvSeries.buzzId,
        mergedTargetId: tvSeries.mergedTargetId,
        seasons: tvSeries.seasons.map((s) => mapToTvSeasonDetails(s))
    };
};

const mapToTvSeasonDetails = (season: CrossTypeContentSearchTvSeriesItemSeason) => {
    return {
        id: season.id,
        name: season.name,
        number: season.number,
        buzzId: season.buzzId,
        mergedTargetId: season.mergedTargetId,
        episodes: season.episodes.map((e) => mapToEpisodeDetails(e))
    };
};

const mapToEpisodeDetails = (episode: CrossTypeContentSearchTvSeriesItemEpisode) => {
    return {
        id: episode.id,
        title: episode.title,
        number: episode.number,
        buzz: episode.buzz && {
            id: episode.buzz.id,
            isArchived: episode.buzz.isArchived,
            isDeleted: episode.buzz.isDeleted
        },
        mergedTargetId: episode.mergedTargetId,
        versions: episode.versions.map((v) => mapToEpisodeVersionDetails(v))
    };
};

const mapToEpisodeVersionDetails = (version: CrossTypeContentSearchTvSeriesItemEpisodeVersion) => {
    return {
        id: version.id,
        type: version.type,
        distributor: version.distributor,
        brandings: version.brandings,
        buzzVersion: version.buzzVersion && {
            id: version.buzzVersion.id,
            isArchived: version.buzzVersion.isArchived,
            name: version.buzzVersion.name,
            notes: version.buzzVersion.notes
        },
        mergedTargetId: version.mergedTargetId
    };
};

// /* ancillary */
const mapToAncillaryDetails = (ancillary: CrossTypeContentSearchAncillaryItem): ContentAssociationAncillaryDetails => {
    return {
        type: ContentReferenceType.Ancillary,
        subtype: ancillary.subtype,
        source: ancillary.source,
        id: ancillary.id,
        title: ancillary.title,
        buzz: ancillary.buzz && {
            id: ancillary.buzz.id,
            isArchived: ancillary.buzz.isArchived,
            isDeleted: ancillary.buzz.isDeleted
        },
        version: {
            id: ancillary.version.id,
            type: ancillary.version.type,
            distributor: ancillary.version.distributor,
            airlines: ancillary.version.airlines,
            buzzId: ancillary.version.buzzId
        },
        isMerged: ancillary.isMerged
    };
};
